import { TFunction } from 'i18next';
import { Trans } from 'next-i18next/pages';
import Link from 'next/link';

import { MAGAZIN_JOB_APPLICATION_TIPS_URL } from '@pflegia/ui/pflegia/constants/routes';
import { FaqObject } from '@pflegia/ui/shared/landing-page/faq-page-json-ld/faq-page-json-ld-base';
import RelatedLocations from '@pflegia/ui/shared/landing-page/questions-and-answers/RelatedLocations';

import { JOB_LISTING_FAQS_BY_VARIANT, JobListingFaqVariant, stripFormattingTagsForSchema } from '../constants/faq';

type TranslationParameters = {
  domain?: string;
  translatedJobTitle?: string;
  location?: string;
};

interface BuildJobListingFaqsProps {
  faqVariant: Exclude<JobListingFaqVariant, 'none'>;
  relatedLocations: string[];
  selectModel: string;
  tJobListings: TFunction;
  translatedJobTitle?: string;
  translationParameters: TranslationParameters;
}

const getFaqKeyPrefix = (faqVariant: Exclude<JobListingFaqVariant, 'none'>, selectModel: string) =>
  faqVariant === 'jobAndLocation' ? `questionsAndAnswers.${selectModel}` : `questionsAndAnswers.${faqVariant}`;

const getFaqSchemaText = (value: string) => stripFormattingTagsForSchema(value).trim();

export const buildJobListingFaqs = ({
  faqVariant,
  relatedLocations,
  selectModel,
  tJobListings,
  translatedJobTitle,
  translationParameters,
}: BuildJobListingFaqsProps): FaqObject[] => {
  const keyPrefix = getFaqKeyPrefix(faqVariant, selectModel);

  return JOB_LISTING_FAQS_BY_VARIANT[faqVariant]
    .filter(faqKey => faqKey !== 'otherPopularLocations' || (relatedLocations?.length ?? 0) > 0)
    .map(faqKey => ({
      question: (
        <Trans
          i18nKey={`${keyPrefix}.${faqKey}.question`}
          t={tJobListings}
          values={translationParameters}
          components={{
            strong: <strong />,
          }}
        />
      ),
      answer: (
        <Trans
          i18nKey={`${keyPrefix}.${faqKey}.answer`}
          t={tJobListings}
          values={translationParameters}
          components={{
            strong: <strong />,
            RelatedLocations: <RelatedLocations relatedLocations={relatedLocations} translatedJobTitle={translatedJobTitle} />,
            magazinJobApplicationTips: (
              <Link
                onClick={event => event.stopPropagation()}
                target='_blank'
                href={MAGAZIN_JOB_APPLICATION_TIPS_URL}
                className='text-primary hover:underline'
              />
            ),
          }}
        />
      ),
      questionForSchema: getFaqSchemaText(
        tJobListings(`${keyPrefix}.${faqKey}.question`, { ...translationParameters, interpolation: { escapeValue: false } }),
      ),
      answerForSchema: getFaqSchemaText(
        tJobListings(`${keyPrefix}.${faqKey}.answer`, { ...translationParameters, interpolation: { escapeValue: false } }),
      ),
    }));
};
